/**
 * @file index.js
 * @description Create all re-usable form schemas here and export them from index so that it can be imported from @/zod directly.
 * @version 0.0.1
 * Created at: 11/08/2025
 * Updated at: 11/08/2025
 */
import { z } from 'zod';

import {
  ACTION_TYPE_ENUMS,
  RESOURCE_TYPE_ENUMS,
  ROLEPLAY_INTRO_TYPE_ENUM,
  ROLEPLAY_LEVEL_ENUM,
  STATUS_ENUM,
} from '@/assets/data';

/**
 * Email Schema
 */
const emailSchema = z
  .email()
  .min(5, { message: 'Email must be required.' })
  .max(255, { message: 'Email must have max 255 characters' });

/**
 * Url Validation
 */
const urlSchema = (key) => {
  return z.url({ required_error: `${key} is required.`, message: `${key} is invalid.` });
};

/**
 * Password Schema
 */
const passwordSchema = z
  .string({ required_error: 'Password must be required.' })
  .min(8, { message: 'Password must be at least 8 characters.' })
  .max(12, { message: 'Password must have max 12 characters' })
  .regex(/[A-Z]/, { message: 'Password must contain at least one uppercase letter.' })
  .regex(/[a-z]/, { message: 'Password must contain at least one lowercase letter.' })
  .regex(/[0-9]/, { message: 'Password must contain at least one number.' })
  .regex(/[^a-zA-Z0-9]/, { message: 'Password must contain at least one special character.' });

/**
 * String Validation
 */
const stringSchema = (key, min = 3, max = 255) => {
  let schema = z.string({ required_error: `${key} is required.` });

  if (min) {
    schema = schema.min(min, { message: `${key} must be at least ${min} characters.` });
  }

  if (max) {
    schema = schema.max(max, { message: `${key} must have max ${max} characters.` });
  }

  return schema;
};

/**
 * Prompt String Validation
 */

export const promptStringSchema = (key = 'Prompt', min = 10, max = false) => {
  return stringSchema(key, min, max).refine((val) => !/[{}]/.test(val), {
    message: `${key} cannot contain { or } characters.`,
  });
};

/**
 * Select Validation
 */
const selectSchema = (key, min = 1) => {
  return z
    .string({
      required_error: `${key} is required.`,
      invalid_type_error: `${key} is required.`,
      message: `${key} is required.`,
    })
    .min(min, { message: `${key} is required.` });
};

// usernameSchema with no spaces only dash and underscore allowed
const usernameSchema = (key) => {
  return z
    .string({ required_error: `${key} is required.` })
    .min(3, { message: `${key} must be at least 3 characters.` })
    .max(20, { message: `${key} must have max 20 characters.` })
    .regex(/^[a-zA-Z0-9_-]+$/, {
      message: `${key} must be alphanumeric and can only contain - and _.`,
    });
};

/**
 * Number Validation
 * @param {string} key - Name of the key.
 * @param {number} [min=0] - Minimum value of the number.
 * @param {number} [max=null] - Maximum value of the number.
 */
const numberSchema = (key, min = 0, max = null) => {
  let schema = z.coerce
    .number({ required_error: `${key} is required.` })
    .min(min, { message: `${key} must be at least ${min}.` });

  if (max !== null) {
    schema = schema.max(max, { message: `${key} must have max ${max}.` });
  }

  return schema;
};

const descriptionSchema = z
  .string()
  .max(255, { message: 'Description must have max 255 characters.' })
  .optional();

/**
 * String Validation
 */
// const fileSchema = (key, sizeInBytes = 2 * 1024 * 1024) => {
//   return z
//     .object({
//       file: z.instanceof(File, { message: `${key} is required` }),
//     })
//     .refine(
//       (val) => val.file.size <= 2 * 1024 * 1024, // 2MB
//       { message: 'File must be less than 2MB' }
//     )
//     .refine((val) => ['image/png', 'image/jpeg', 'image/jpg'].includes(val.file.type), {
//       message: 'Only PNG or JPG images are allowed',
//     })
//     .nullable() // if avatar is optional
//     .refine((val) => val !== null, { message: 'Avatar is required' });
// };

export const booleanSchema = (key) => {
  return z.stringbool({
    required_error: `${key} is required.`,
    invalid_type_error: `${key} must be a boolean (true/false).`,
    message: `${key} must be a boolean (true/false).`,
  });
};

export const statusSchema = (key) => {
  return z.enum(
    STATUS_ENUM?.map((s) => s.value),
    {
      required_error: `${key} is required.`,
      invalid_type_error: `${key} is invalid.`,
      message: `${key} is required.`,
    }
  );
};

export const resourceSchema = (key) => {
  return z.enum(
    RESOURCE_TYPE_ENUMS?.map((s) => s.value),
    {
      required_error: `${key} is required.`,
      invalid_type_error: `${key} is invalid.`,
      message: `${key} is required.`,
    }
  );
};

export const actionSchema = (key) => {
  return z.enum(
    ACTION_TYPE_ENUMS?.map((s) => s.value),
    {
      required_error: `${key} is required.`,
      invalid_type_error: `${key} is invalid.`,
      message: `${key} is required.`,
    }
  );
};

/**
 * Login Form Schema
 */
export const loginFormSchema = z.object({
  email: emailSchema,
  password: stringSchema('Password', 1),
});

/**
 * User profile form schema
 */
export const userProfileFormSchema = z.object({
  first_name: stringSchema('First name', 3),
  last_name: stringSchema('Last name', 3),
  email: emailSchema,
  // avatar: fileSchema('avatar', 2 * 1024),
});

// Confirm password schema
const confirmPasswordSchema = z.string({
  required_error: 'Please confirm your password',
});

export const organizationSchema = z.object({
  // logo: fileSchema('Logo', 2 * 1024).optional(),
  name: stringSchema('Organization name', 2),
  slug: stringSchema('Organization slug', 2),
  website: urlSchema('Organization website url'),
  description: stringSchema('Description', 1, false),
});

export const organizationCreateFromSchema = z
  .object({
    organization: organizationSchema,
    user: z.object({
      name: stringSchema('Name'),
      email: emailSchema,
      password: passwordSchema,
      confirmPassword: confirmPasswordSchema,
      role: stringSchema('Role', 1),
    }),
  })
  .refine((data) => data.user.password === data.user.confirmPassword, {
    message: "Passwords don't match",
    path: ['user.confirmPassword'],
  });

// Sign Up Form Schema with password confirmation
export const signUpFormSchema = z
  .object({
    name: stringSchema('Name'),
    email: emailSchema,
    password: passwordSchema,
    confirmPassword: confirmPasswordSchema,
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: "Passwords don't match",
    path: ['confirmPassword'],
  });

export const organizationContactsSchema = z.object({
  email: emailSchema,
  name: stringSchema('name', 2),
  phone: stringSchema('phone', 10),
  role: stringSchema('role', 1),
  description: stringSchema('description', 1, false),
});

export const organizationUserSchema = z
  .object({
    email: emailSchema,
    name: stringSchema('Name', 3),
    role: selectSchema('Role'),
    password: passwordSchema,
    confirmPassword: confirmPasswordSchema,
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: "Passwords don't match",
    path: ['confirmPassword'],
  });

export const inviteUserToOrganizationSchema = z.object({
  role: stringSchema('Role is required.', 1),
  user: stringSchema('User is required.', 1),
  message: stringSchema('Message is required.', 1, 255),
});

export const subscriptionPlansSchema = z.object({
  name: stringSchema('Name', 2),
  description: stringSchema('Description', 10),
  startDate: z.date({ required_error: 'Start date is required.' }),
  endDate: z.date({ required_error: 'End date is required.' }),
  maxUsers: stringSchema('Max users is required.', 1),
});

export const createNewBlockSchema = z.object({
  name: stringSchema('Name is required.', 2),
  description: descriptionSchema,
});

export const createNewVariableSchema = z.object({
  name: stringSchema('Name is required.', 2),
  description: descriptionSchema,
  type: stringSchema('Type is required.', 1),
  key: usernameSchema('Key'),
});

export const createNewVariableOptionSchema = z.object({
  name: stringSchema('Title', 2),
  key: usernameSchema('Code'),
});

/**
 * Create role schema
 */
export const roleSchema = z.object({
  name: stringSchema('Name', 3),
  description: descriptionSchema,
  isSystemRole: booleanSchema('Is system role'),
  status: statusSchema('Status').optional().default('active'),
});

/**
 * Create permission schema
 */
export const permissionSchema = z.object({
  name: stringSchema('Name', 3),
  resource: resourceSchema('Resource'),
  action: actionSchema('Action'),
});

/**
 * Assign permission to role schema
 */
export const assignPermissionToRoleSchema = z.object({
  permissions: z.array(z.string()).min(1, 'At least one permission is required'),
});

export const roleplaySetupSchema = z.object({
  name: stringSchema('Title', 3),
  description: stringSchema('Roleplay description', 3, 500),
  category: stringSchema('Roleplay category', 3),
});

export const courseSchema = z.object({
  title: stringSchema('Course', 3),
  description: stringSchema('Course description', 3, 500),
  category: stringSchema('Course category', 3),
});

export const roleplayLandingForm = z.object({
  name: stringSchema('Title', 3, 255),
  description: stringSchema('Description', 3, false),
  category: stringSchema('Category', 3, 255),
  subCategory: stringSchema('Sub category', false, 255).optional(),
  level: z
    .enum(
      ROLEPLAY_LEVEL_ENUM?.map((r) => r.value),
      {
        required_error: 'Please select a level.',
      }
    )
    .default('beginner'),
  introType: z.enum(
    ROLEPLAY_INTRO_TYPE_ENUM?.map((r) => r.value),
    {
      required_error: 'Select an intro type.',
    }
  ),
  introText: z.string().optional(),
  image: z.any().optional(),
  introContentData: z.any().optional(),
  generate_score: z.boolean().optional(),
});

export const aiCharacterCustomizationForm = z.object({
  aiAvatarId: selectSchema('Select anam avatar', 1),
  voiceAvatarId: selectSchema('Select voice', 3, 255),
  avatarName: stringSchema('Name', 3, false),
  aiCharacterFirstLine: stringSchema('AI character first line', 3),
  llm: z.string().optional(),
  // maxTimeAllowed: numberSchema('Max time allowed', 1),
  // llm: selectSchema('Select LLM model', 1),
});

export const userGroupFormSchema = z.object({
  name: stringSchema('Name', 3, 255),
  description: stringSchema('Description', 3, 255),
  users: z.array(z.string()).min(1, 'At least one user is required'),
});

export const addUserIntoGroupSchema = z.object({
  users: z.array(z.string()).min(1, 'At least one user is required'),
});

export const createCompetencySchema = z.object({
  name: stringSchema('Competency name', 3, 255),
  description: stringSchema('Description', 3, 50),
  additional_info: stringSchema('Additional info', false, 50, true),
});

export const roleplayDeploymentSchema = z.object({
  userGroupId: selectSchema('Select user group'),
  isNotify: z.boolean({
    required_error: 'Is notify is required.',
    message: 'Is notify is required.',
  }),
  isCompleted: z
    .boolean({
      required_error: 'Is completed is required.',
      message: 'Is completed is required.',
    })
    .default(false),
  deployedAt: z.date({
    required_error: 'Schedule date is required.',
    message: 'Schedule date is required.',
    invalid_type_error: 'Schedule date is invalid.',
  }),
});

export const createPromptSchema = z.object({
  name: z.string().min(1, 'Name is required'),
  description: z.string().min(1, 'Description is required'),
});

export const forgotPasswordSchema = z.object({
  email: emailSchema,
});

export const otpVerificationSchema = z.object({
  otp: z.string().min(6, {
    message: 'Your one-time password must be 6 characters.',
  }),
});

export const resetPasswordSchema = z
  .object({
    password: passwordSchema,
    confirmPassword: confirmPasswordSchema,
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: "Password don't match",
    path: ['confirmPassword'],
  });

export const assessmentCreateFormSchema = z.object({
  title: stringSchema('Title', 2),
  description: stringSchema('Description', 5),
  organizationIds: z.array(z.string()).optional(),
  promptId: stringSchema('Prompt', false, false).optional(),
  status: selectSchema('Status', 1),
  prompt_text: promptStringSchema('Prompt', 10, false),
});

export const createSystemPromptSchema = z.object({
  title: stringSchema('Title', 3, 225),
  description: stringSchema('Description', 10, false),
  prompt: z.string().min(8, {
    message: 'Prompt must be required.',
  }),
});

export const bugReportSchema = z.object({
  title: z.string().min(3, 'Title is required'),
  email: emailSchema,
  description: z
    .string()
    .min(10, 'Description must be at least 10 characters')
    .max(250, 'Description must be at most 250 characters'),
  type: z.enum(['bug', 'feature_request', 'improvement'], {
    required_error: 'Type is required',
  }),
  images: z.array(z.string().url()).optional(),
});

export const createCategorySchema = z.object({
  title: z.string().min(3, 'Title is required'),
});

export const updateBugSchema = z.object({
  status: z.string().min(3, 'Title is required'),
  title: z.string().min(3, 'Title is required'),
  description: z
    .string()
    .min(10, 'Description must be at least 10 characters')
    .max(250, 'Description must be at most 250 characters'),
  type: z.enum(['bug', 'feature_request', 'improvement'], {
    required_error: 'Type is required',
  }),
  priority: z.string(),
  feedback_text: z.string().max(500, 'Feedback text must be at most 500 characters'),
  is_duplicate: z.boolean(),
});


export const promptDeploymentSchema = z.object({
  image: z.string().min(1, { message: "Image is required" }),
  assessmentCriteriaIds: z.array(z.string()).min(1, {
    message: "At least one assessment criteria is required"
  })
});
